import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@wa/ui";
import { Pop } from "../shell/Pop";
import type { MenuOption } from "./MenuField";

/**
 * A many-of-N choice wearing the same dress as `MenuField`.
 *
 * `MenuField` beside it picks one value and closes. `FilterMenu` collects
 * several but wears the toolbar pill, which is right above a table and wrong in
 * a form: it shows only its label and a count badge, so a field whose column
 * heading already says "Status" would read "Status 2" and never say which two.
 * This one shows the choice itself - the option's own label at one tick, a
 * count from two - and stays open while the operator ticks, because picking one
 * of a many-of-N is rarely the whole thought.
 *
 * An empty selection is "all", not "none": no tick is no filter. Saying so on
 * the trigger is the whole reason `allLabel` is required rather than defaulted -
 * a blank field would read as broken.
 *
 * Values with no matching option are ignored rather than counted. That is what
 * makes it safe to leave a stale status in state for the render between a kind
 * change and the effect that prunes it: the trigger never claims a tick the
 * open menu cannot show.
 *
 * Must be rendered inside a `PopHost`, like every other menu in the app.
 */
export function MultiMenuField<T extends string>({
  value,
  onChange,
  options,
  label,
  allLabel,
  countLabel,
  clearLabel,
  variant = "toolbar",
  className,
}: {
  value: readonly T[];
  onChange: (next: T[]) => void;
  options: MenuOption<T>[];
  /** Accessible name - the trigger only shows what is picked. */
  label: string;
  /** What the trigger reads when nothing is ticked. */
  allLabel: string;
  /** The trigger's text from two ticks up. One tick shows the option itself. */
  countLabel: (n: number) => string;
  /** The row at the foot of the menu that unticks everything. */
  clearLabel: string;
  variant?: "toolbar" | "form";
  className?: string;
}) {
  const isForm = variant === "form";
  // Read off `options` rather than off `value`, so the summary follows the menu
  // order and can never name a value the menu does not offer.
  const picked = options.filter((o) => value.includes(o.value));
  const summary = picked.length === 0 ? allLabel : picked.length === 1 ? picked[0]!.label : countLabel(picked.length);

  const toggle = (v: T) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  return (
    <Pop
      matchWidth={isForm}
      trigger={(open) => (
        <button
          type="button"
          className={cn("crm-fld crm-fld-sel", isForm && "crm-fld-form", open && "is-on", className)}
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <span className="crm-trunc flex-1 text-left">{summary}</span>
          <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
        </button>
      )}
    >
      {options.map((o) => {
        const on = value.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            role="menuitemcheckbox"
            aria-checked={on}
            className={cn("crm-mi", on && "is-on")}
            onClick={() => toggle(o.value)}
          >
            {o.icon}
            <span className="crm-trunc flex-1 text-left">{o.label}</span>
            {/* Kept in the layout when off, or every row shifts sideways as the
                tick appears and disappears under the pointer. */}
            <Check className={cn("size-4", !on && "opacity-0")} />
          </button>
        );
      })}

      {value.length > 0 ? (
        <>
          <div className="crm-menu-sep" />
          <button type="button" className="crm-mi" onClick={() => onChange([])}>
            <X className="size-4" />
            {clearLabel}
          </button>
        </>
      ) : null}
    </Pop>
  );
}
